import { LocalOfferLocation, OfferSource } from '@varo/graphql-schema-types';
import { RoutePath, useRouter } from 'expo-router';
import NearbyOfferCard from 'features/rewards/components/NearbyOfferCard/NearbyOfferCard';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import Button from 'shared/components/vxl/Button/Button';
import theme from 'shared/styles/theme';

interface NearbyOffersCarouselProps {
  nearbyOffers: LocalOfferLocation[];
  coordinates?: google.maps.LatLngLiteral;
}

const NearbyOffersCarousel = ({
  nearbyOffers,
  coordinates,
}: NearbyOffersCarouselProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'nearbyOffersCarousel',
  });

  const onCardPress = (offer: LocalOfferLocation) => {
    if (offer.offerSource === OfferSource.Upside) {
      if (!offer.merchantId) {
        return;
      }

      const params: {
        offerid: string;
        merchantId: string;
        lat?: string;
        lng?: string;
      } = {
        offerid: offer.id,
        merchantId: offer.merchantId,
      };

      if (coordinates) {
        params.lat = coordinates.lat.toString();
        params.lng = coordinates.lng.toString();
      }

      router.push({
        pathname: '/rewards/upside-offers/[merchantId]',
        params,
      });
      return;
    }

    if (offer.onClick?.route) {
      router.push(offer.onClick.route as RoutePath);
    }
  };

  return (
    <View
      style={[
        styles.container,
        nearbyOffers.length > 0 && styles.containerWithOffers,
      ]}
    >
      <FlatList
        contentContainerStyle={styles.standardCardCarousel}
        data={nearbyOffers}
        renderItem={({ item }) => (
          <NearbyOfferCard
            iconUrl={item.logoImage}
            titleText={item.name}
            subtitleText={item.cashbackAmount}
            onCardPress={() => onCardPress(item)}
            analyticsId="partnerOfferSelected"
            customAttributes={
              item.onClick?.analyticsEvent
                ? parseGQLAnalyticsEvent(item.onClick.analyticsEvent).properties
                : null
            }
          />
        )}
        keyExtractor={(item) => item.id.toString()}
        horizontal
        showsHorizontalScrollIndicator={false}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
        removeClippedSubviews={false}
        ListFooterComponent={
          nearbyOffers.length > 0 ? (
            <View style={styles.buttonContainer}>
              <Button
                label={translatedText('viewAll')}
                type="SECONDARY"
                onPress={() => router.push('/rewards/nearby-offers/map')}
              />
            </View>
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.spacing.s,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: 'transparent',
  },
  containerWithOffers: {
    paddingBottom: theme.spacing.m,
  },
  separator: {
    backgroundColor: 'transparent',
    width: theme.spacing.s,
  },
  standardCardCarousel: {
    paddingEnd: theme.spacing.m,
    paddingStart: theme.spacing.m,
  },
});

export default NearbyOffersCarousel;
